import React, { useState } from 'react';
import axios from 'axios';
import {
  Grid,
  TextField,
  Box,
  Typography,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { AttachFile, Download } from '@mui/icons-material';
import { useToast } from '../../../contexts/ToastContext';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../../utils/timezoneUtils';
import { tlbFormatFieldProps, formatTechlogOilQuantityLabel } from '../../../utils/technicalLogBookFieldFormat';
import {
  USAGE_VALUE_TYPE_OPTIONS,
  MAINTENANCE_AT_VALUE_TYPE_OPTIONS,
  getHeaderFieldSections,
  getHeaderFields
} from '../../../constants/technicalLogBookDefinition';

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split('.');
  let v = obj;
  for (const k of keys) {
    v = v?.[k];
  }
  return v;
};

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const dt = dayjs(value);
  if (!dt.isValid()) return '';
  return dt.format('YYYY-MM-DDTHH:mm');
};

const TechlogProofField = ({ label, proof, entryId, allowEdit, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const { showToast } = useToast();
  const hasFile = proof && proof.fileUrl;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !entryId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`/api/technical-log-book/${entryId}/techlog-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Techlog proof uploaded', 'success');
      onRefresh?.();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!entryId) return;
    setRemoving(true);
    try {
      await axios.delete(`/api/technical-log-book/${entryId}/techlog-proof`);
      showToast('Techlog proof removed', 'success');
      onRefresh?.();
    } catch (err) {
      showToast(err.response?.data?.message || 'Remove failed', 'error');
    } finally {
      setRemoving(false);
    }
  };

  const uploader =
    proof?.uploadedBy &&
    typeof proof.uploadedBy === 'object' &&
    (proof.uploadedBy.firstName || proof.uploadedBy.lastName)
      ? `${proof.uploadedBy.firstName || ''} ${proof.uploadedBy.lastName || ''}`.trim()
      : null;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      {hasFile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="body2">{proof.originalFileName || 'Attachment'}</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            href={proof.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
          >
            Download
          </Button>
          {allowEdit && (
            <Button size="small" color="error" variant="text" onClick={handleRemove} disabled={removing}>
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          )}
        </Box>
      )}
      {hasFile && uploader && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Uploaded by {uploader}
          {proof.uploadedAt ? ` · ${dayjs(proof.uploadedAt).format('DD MMM YYYY, HH:mm')}` : ''}
        </Typography>
      )}
      {allowEdit && (
        <Button variant="outlined" size="small" component="label" startIcon={<AttachFile />} disabled={uploading}>
          {uploading ? 'Uploading…' : hasFile ? 'Replace file' : 'Upload file'}
          <input
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
            onChange={handleUpload}
          />
        </Button>
      )}
      {!hasFile && !allowEdit && <Typography variant="body2">Not set</Typography>}
    </Box>
  );
};

const formatUsageValueTypeLabel = (valueType, options = USAGE_VALUE_TYPE_OPTIONS) => {
  const match = options.find((opt) => opt.value === valueType);
  return match?.label || valueType || '';
};

const UsageValueField = ({
  label,
  basePath,
  valueData,
  disabled,
  required,
  options = USAGE_VALUE_TYPE_OPTIONS,
  updateField,
  readOnly = false
}) => {
  const valueType = valueData?.valueType ?? '';
  const value = valueData?.value ?? '';

  if (readOnly) {
    const typeLabel = formatUsageValueTypeLabel(valueType, options);
    const displayValue = value != null && value !== '' ? String(value) : 'Not set';
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" gutterBottom>{label}</Typography>
        <Paper sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
          <Typography variant="body2">
            {typeLabel ? `${typeLabel}: ${displayValue}` : displayValue}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <FormControl fullWidth disabled={disabled} required={required} sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={valueType}
            label="Type"
            onChange={(e) => updateField(`${basePath}.valueType`, e.target.value || undefined)}
          >
            <MenuItem value="">
              <em>Select type</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label ?? opt.value}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Value"
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (valueType === 'HOURS') {
              updateField(`${basePath}.value`, raw);
              return;
            }
            updateField(`${basePath}.value`, raw === '' ? undefined : Number(raw));
          }}
          disabled={disabled || !valueType}
          required={required && !!valueType}
          type={valueType === 'HOURS' ? 'text' : 'number'}
          inputProps={valueType === 'HOURS' ? undefined : { min: 0, step: valueType === 'CYCLES' ? 'any' : 1 }}
          {...(valueType === 'HOURS' ? tlbFormatFieldProps(value, { fieldType: 'hours' }) : {})}
        />
      </Stack>
    </Box>
  );
};

const HeaderTab = ({
  formData,
  isReadOnly,
  updateField,
  canEdit,
  headerSection,
  displayTimezone,
  onRefreshEntry
}) => {
  const useDefinition = headerSection && headerSection.visible !== false && (headerSection.fields?.length > 0 || headerSection.customFields?.length > 0 || headerSection.fieldSections?.length > 0);
  const headerFieldsList = getHeaderFields(headerSection);
  const headerFieldSections = getHeaderFieldSections(headerSection);
  const headerShowsTechlogProof = headerFieldsList.some((f) => f.path === 'techlogProof' && f.visible !== false);
  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const toDisplayDate = (v) => {
    if (!v || !displayTimezone) return null;
    const d = utcToDisplayDateAndTime(v, displayTimezone).date;
    return d ? dayjs(d) : null;
  };
  const toUTC = (dateStr) => {
    if (!dateStr || !displayTimezone) return null;
    return displayDateTimeToUTC(dateStr, '00:00', displayTimezone);
  };

  const normalizeHoursValue = (raw) => {
    if (!raw) return '';
    let value = String(raw).trim();
    if (!value) return '';
    if (!value.includes(':')) {
      value = value.slice(0, 6);
      if (!value) return '';
      return `${value}:00`;
    }
    const [hRaw, mRaw = ''] = value.split(':');
    const hours = hRaw.replace(/\D/g, '').slice(0, 6);
    const minutes = mRaw.replace(/\D/g, '').slice(0, 4);
    if (!hours) return '';
    return minutes ? `${hours}:${minutes}` : hours;
  };

  const renderFieldControl = (field, path, isCustom = false) => {
    const value = getValueByPath(formData, path);
    const disabled = isReadOnly || field.readOnly;
    const required = !!field.required;
    const label = formatTechlogOilQuantityLabel(field.label || path, path);

    if (field.readOnly && field.type === 'file') {
      const proof = value && typeof value === 'object' ? value : null;
      return (
        <Box key={path}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {label}
          </Typography>
          {proof?.fileUrl ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              href={proof.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              component="a"
            >
              {proof.originalFileName || 'Download'}
            </Button>
          ) : (
            <Typography variant="body2">Not set</Typography>
          )}
        </Box>
      );
    }

    if (field.readOnly && field.type === 'dueValue') {
      return (
        <UsageValueField
          key={path}
          label={label}
          basePath={path}
          valueData={getValueByPath(formData, path) || {}}
          disabled
          options={field.options || USAGE_VALUE_TYPE_OPTIONS}
          updateField={updateField}
          readOnly
        />
      );
    }

    if (field.readOnly) {
      return (
        <Box key={path}>
          <Typography variant="caption" color="text.secondary" gutterBottom>{label}</Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
            <Typography variant="body2">{value != null && value !== '' ? String(value) : 'Not set'}</Typography>
          </Paper>
        </Box>
      );
    }

    switch (field.type) {
      case 'multiline':
        return (
          <TextField
            key={path}
            fullWidth
            multiline
            minRows={2}
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            disabled={disabled}
            required={required}
          />
        );
      case 'number':
        return (
          <TextField
            key={path}
            fullWidth
            type="number"
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value === '' ? undefined : Number(e.target.value))}
            disabled={disabled}
            required={required}
          />
        );
      case 'date':
        return (
          <LocalizationProvider key={path} dateAdapter={AdapterDayjs}>
            <DatePicker
              label={tzLabel ? `${label} (${tzLabel})` : label}
              value={displayTimezone ? toDisplayDate(value) : (value ? dayjs(value) : null)}
              onChange={(date) => updateField(path, date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
              disabled={disabled}
              slotProps={{ textField: { fullWidth: true, required } }}
            />
          </LocalizationProvider>
        );
      case 'datetime':
        return (
          <TextField
            key={path}
            fullWidth
            type="datetime-local"
            label={tzLabel ? `${label} (${tzLabel})` : label}
            value={toDateTimeLocalValue(value)}
            onChange={(e) => updateField(path, e.target.value ? new Date(e.target.value) : null)}
            disabled={disabled}
            required={required}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'checkbox':
        return (
          <FormControlLabel
            key={path}
            control={
              <Switch
                checked={!!value}
                onChange={(e) => updateField(path, e.target.checked)}
                disabled={disabled}
              />
            }
            label={label}
          />
        );
      case 'dropdown':
        return (
          <FormControl key={path} fullWidth disabled={disabled} required={required}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={value ?? ''}
              label={label}
              onChange={(e) => updateField(path, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label ?? opt.value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'hours':
        return (
          <TextField
            key={path}
            fullWidth
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            onBlur={(e) => updateField(path, normalizeHoursValue(e.target.value))}
            disabled={disabled}
            required={required}
            {...tlbFormatFieldProps(value, { fieldType: 'hours' })}
          />
        );
      case 'file':
        return (
          <TechlogProofField
            key={path}
            label={label}
            proof={value && typeof value === 'object' ? value : null}
            entryId={formData?._id}
            allowEdit={canEdit && !isReadOnly && !field.readOnly}
            onRefresh={onRefreshEntry}
          />
        );
      case 'dueValue':
        return (
          <UsageValueField
            key={path}
            label={label}
            basePath={path}
            valueData={getValueByPath(formData, path) || {}}
            disabled={disabled}
            required={required}
            options={field.options || USAGE_VALUE_TYPE_OPTIONS}
            updateField={updateField}
            readOnly={disabled}
          />
        );
      default:
        return (
          <TextField
            key={path}
            fullWidth
            label={label}
            value={value ?? ''}
            onChange={(e) => updateField(path, e.target.value)}
            disabled={disabled}
            required={required}
          />
        );
    }
  };

  if (useDefinition) {
    return (
      <Grid container spacing={2}>
        {headerFieldsList.filter((f) => f.visible !== false).map((field) => (
          <Grid
            item
            xs={12}
            md={field.type === 'multiline' || field.type === 'file' || field.type === 'dueValue' ? 12 : 6}
            key={field.path}
          >
            {renderFieldControl(field, field.path, false)}
          </Grid>
        ))}
        {(headerSection.customFields || []).filter((f) => f.visible !== false).map((field) => (
          <Grid item xs={12} md={6} key={field.key}>
            {renderFieldControl(field, `additionalFields.${field.key}`, true)}
          </Grid>
        ))}
        {headerFieldSections.filter((section) => section.visible !== false).map((section) => (
          <Grid item xs={12} key={section.id}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>{section.label}</Typography>
            <Grid container spacing={2}>
              {(section.fields || []).filter((f) => f.visible !== false).map((field) => (
                <Grid
                  item
                  xs={12}
                  md={field.type === 'dueValue' || field.type === 'multiline' ? 12 : 6}
                  key={field.path || field.key}
                >
                  {renderFieldControl(field, field.path, false)}
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
        {!headerShowsTechlogProof && (
          <Grid item xs={12}>
            <TechlogProofField
              label="Techlog proof"
              proof={formData.techlogProof}
              entryId={formData._id}
              allowEdit={canEdit && !isReadOnly}
              onRefresh={onRefreshEntry}
            />
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Sheet Number"
          value={formData.sheetNumber || ''}
          onChange={(e) => updateField('sheetNumber', e.target.value)}
          disabled={isReadOnly}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Operator Name"
          value={formData.operatorName || ''}
          onChange={(e) => updateField('operatorName', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Issue Format Revision
          </Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
            <Typography variant="body2">
              {formData.issueFormatRevision || 'Not set'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Read-only (from system configuration)
            </Typography>
          </Paper>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Aircraft Type"
          value={formData.aircraftType || ''}
          onChange={(e) => updateField('aircraftType', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Aircraft Registration"
          value={formData.aircraftRegistration || ''}
          onChange={(e) => updateField('aircraftRegistration', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Aircraft Serial Number"
          value={formData.aircraftSerialNumber || ''}
          onChange={(e) => updateField('aircraftSerialNumber', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={tzLabel ? `Entry Date (${tzLabel})` : 'Entry Date'}
            value={displayTimezone ? toDisplayDate(formData.entryDate) : (formData.entryDate ? dayjs(formData.entryDate) : null)}
            onChange={(date) => updateField('entryDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
            disabled={isReadOnly}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="datetime-local"
          label={tzLabel ? `BA Test Time (${tzLabel})` : 'BA Test Time'}
          value={toDateTimeLocalValue(formData.baTestTime)}
          onChange={(e) => updateField('baTestTime', e.target.value ? new Date(e.target.value) : null)}
          disabled={isReadOnly}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Maintenance Details"
          value={formData.maintenanceDetails || ''}
          onChange={(e) => updateField('maintenanceDetails', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Work Order Number"
          value={formData.workOrderNumber || ''}
          onChange={(e) => updateField('workOrderNumber', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={tzLabel ? `Work Order Date (${tzLabel})` : 'Work Order Date'}
            value={displayTimezone ? toDisplayDate(formData.workOrderDate) : (formData.workOrderDate ? dayjs(formData.workOrderDate) : null)}
            onChange={(date) => updateField('workOrderDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
            disabled={isReadOnly}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="CRS Number"
          value={formData.crsNumber || ''}
          onChange={(e) => updateField('crsNumber', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={tzLabel ? `CRS Date (${tzLabel})` : 'CRS Date'}
            value={displayTimezone ? toDisplayDate(formData.crsDate) : (formData.crsDate ? dayjs(formData.crsDate) : null)}
            onChange={(date) => updateField('crsDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
            disabled={isReadOnly}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12}>
        <UsageValueField
          label="CRS Value"
          basePath="crsValue"
          valueData={formData.crsValue || {}}
          disabled={isReadOnly}
          updateField={updateField}
          readOnly={isReadOnly}
        />
      </Grid>
      <Grid item xs={12}>
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            CRS Text
          </Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {formData.crsText || 'Not set'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Read-only
            </Typography>
          </Paper>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="AMO Name"
          value={formData.amoName || ''}
          onChange={(e) => updateField('amoName', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12}>
        <TechlogProofField
          label="Techlog proof"
          proof={formData.techlogProof}
          entryId={formData._id}
          allowEdit={canEdit && !isReadOnly}
          onRefresh={onRefreshEntry}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Component Due</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={tzLabel ? `Due Date (${tzLabel})` : 'Due Date'}
            value={displayTimezone ? toDisplayDate(formData.componentDue?.dueDate) : (formData.componentDue?.dueDate ? dayjs(formData.componentDue.dueDate) : null)}
            onChange={(date) => updateField('componentDue.dueDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
            disabled={isReadOnly}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <UsageValueField
          label="Due Value"
          basePath="componentDue"
          valueData={formData.componentDue || {}}
          disabled={isReadOnly}
          updateField={updateField}
          readOnly={isReadOnly}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Next Scheduled Maintenance</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Description"
          value={formData.nextScheduledMaintenance?.description || ''}
          onChange={(e) => updateField('nextScheduledMaintenance.description', e.target.value)}
          disabled={isReadOnly}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={tzLabel ? `Due Date (${tzLabel})` : 'Due Date'}
            value={displayTimezone ? toDisplayDate(formData.nextScheduledMaintenance?.dueDate) : (formData.nextScheduledMaintenance?.dueDate ? dayjs(formData.nextScheduledMaintenance.dueDate) : null)}
            onChange={(date) => updateField('nextScheduledMaintenance.dueDate', date ? (displayTimezone ? toUTC(date.format('YYYY-MM-DD')) : date.toDate()) : null)}
            disabled={isReadOnly}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={6}>
        <UsageValueField
          label="At Hrs/Cyc/Landings"
          basePath="nextScheduledMaintenance.atValue"
          valueData={formData.nextScheduledMaintenance?.atValue || {}}
          disabled={isReadOnly}
          options={MAINTENANCE_AT_VALUE_TYPE_OPTIONS}
          updateField={updateField}
          readOnly={isReadOnly}
        />
      </Grid>
      {/* Physical Copy Receipt Status */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Physical Copy Receipt Status</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.whiteCopyReceived || false}
              onChange={(e) => updateField('whiteCopyReceived', e.target.checked)}
              disabled={isReadOnly}
            />
          }
          label="White Copy (CAMO) Received"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.yellowCopyReceived || false}
              onChange={(e) => updateField('yellowCopyReceived', e.target.checked)}
              disabled={isReadOnly}
            />
          }
          label="Yellow Copy (Operations) Received"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.pinkCopyReceived || false}
              onChange={(e) => updateField('pinkCopyReceived', e.target.checked)}
              disabled={isReadOnly}
            />
          }
          label="Pink Copy (Aircraft) Received"
        />
      </Grid>
      
    </Grid>
  );
};

export default HeaderTab;

